import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, X, RefreshCcw, Upload } from "lucide-react";

const REGION_ID = "qr-scanner-region";

export default function QRScannerDialog({ open, onOpenChange, onDetected }) {
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | starting | scanning | error
  const [decoding, setDecoding] = useState(false);

  useEffect(() => {
    if (!open) {
      stop();
      return;
    }
    // Delay so the DialogContent has mounted the #qr-scanner-region div before Html5Qrcode tries to read it.
    const id = setTimeout(start, 80);
    return () => { clearTimeout(id); stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const extractAccount = (text) => {
    const m = String(text).match(/(?:account=)?(\d{10})/);
    return m ? m[1] : null;
  };

  const trySecureContext = () => {
    if (typeof window === "undefined") return true;
    return window.isSecureContext || window.location.hostname === "localhost";
  };

  const start = async () => {
    setError("");
    setPhase("starting");

    if (!trySecureContext()) {
      setError("Camera access requires HTTPS. Open this site over a secure connection.");
      setPhase("error");
      return;
    }
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("Your browser does not expose the camera API. Try the latest Chrome or Safari.");
      setPhase("error");
      return;
    }

    // Wait for the scanner region DIV
    const region = document.getElementById(REGION_ID);
    if (!region) {
      setError("Scanner could not initialise. Close and reopen the dialog.");
      setPhase("error");
      return;
    }

    try {
      const html5Qr = new Html5Qrcode(REGION_ID, { verbose: false });
      scannerRef.current = html5Qr;

      const onSuccess = (decoded) => {
        const acc = extractAccount(decoded);
        if (acc) {
          onDetected?.(acc);
          toast.success("Account scanned.");
          stop();
          onOpenChange(false);
        }
      };

      // Try rear camera first via facingMode constraint
      try {
        await html5Qr.start(
          { facingMode: { exact: "environment" } },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          onSuccess,
          () => {}
        );
        setPhase("scanning");
        return;
      } catch (envErr) {
        // Fallback: any camera (front-facing on laptops)
        try {
          await html5Qr.start(
            { facingMode: "user" },
            { fps: 10, qrbox: { width: 240, height: 240 } },
            onSuccess,
            () => {}
          );
          setPhase("scanning");
          return;
        } catch (anyErr) {
          throw anyErr;
        }
      }
    } catch (e) {
      const msg = String(e?.message || e || "");
      let friendly;
      if (/Permission|NotAllowed|denied/i.test(msg)) {
        friendly = "Camera permission denied. In your browser settings → Site permissions → allow Camera for this site, then tap Retry.";
      } else if (/NotFound|no.*device/i.test(msg)) {
        friendly = "No camera was found on this device.";
      } else if (/NotReadable|in use|Could not start/i.test(msg)) {
        friendly = "Camera is being used by another app. Close other camera apps and tap Retry.";
      } else {
        friendly = msg || "Could not start the camera.";
      }
      setError(friendly);
      setPhase("error");
    }
  };

  const stop = async () => {
    const sc = scannerRef.current;
    if (!sc) return;
    try {
      if (sc.isScanning) await sc.stop();
      await sc.clear();
    } catch (e) { /* ignore */ }
    scannerRef.current = null;
  };

  const onUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onFilePicked = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // reset so picking the same file twice re-fires
    if (!file) return;
    setDecoding(true);
    setError("");
    // Stop any running camera so html5-qrcode can scan from a file
    await stop();
    try {
      const tmp = new Html5Qrcode(REGION_ID, { verbose: false });
      const decoded = await tmp.scanFile(file, false);
      await tmp.clear();
      const acc = extractAccount(decoded);
      if (acc) {
        onDetected?.(acc);
        toast.success("Account scanned from image.");
        onOpenChange(false);
      } else {
        setError("That image contained a QR but not a FinVault account number.");
        setPhase("error");
      }
    } catch (e) {
      setError("No QR code found in that image. Make sure it's clear and well-lit, then try again.");
      setPhase("error");
    } finally {
      setDecoding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stop(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="qr-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="w-5 h-5" /> Scan a FinVault QR</DialogTitle>
        </DialogHeader>
        <div className="mt-2 space-y-3">
          <div className="text-xs text-[#4F4D45] leading-relaxed">
            Point your camera at someone's FinVault QR code. Their account number will auto-fill below.
          </div>
          <div id={REGION_ID} className="rounded-xl overflow-hidden bg-black aspect-square relative" data-testid="qr-scanner-region">
            {phase === "starting" && (
              <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">Starting camera…</div>
            )}
            {phase === "error" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white/80 text-sm">
                <Camera className="w-8 h-8 mb-2 opacity-60" />
                <span data-testid="qr-scanner-error">{error}</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Button
              onClick={async () => { await stop(); start(); }}
              variant="outline"
              className="border-[#D2CDBE]"
              data-testid="qr-scanner-retry"
              disabled={phase === "starting" || decoding}
            >
              <RefreshCcw className="w-4 h-4 mr-1.5" /> Retry
            </Button>
            <Button
              onClick={onUploadClick}
              className="bg-[#1B3526] hover:bg-[#0F2517]"
              data-testid="qr-scanner-upload"
              disabled={decoding}
            >
              <Upload className="w-4 h-4 mr-1.5" /> {decoding ? "Reading…" : "Upload"}
            </Button>
            <Button
              onClick={() => { stop(); onOpenChange(false); }}
              variant="outline"
              className="border-[#D2CDBE]"
              data-testid="qr-scanner-cancel"
              disabled={decoding}
            >
              <X className="w-4 h-4 mr-1.5" /> Close
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onFilePicked}
            className="hidden"
            data-testid="qr-scanner-file-input"
          />
          <div className="text-[11px] text-[#4F4D45] text-center leading-relaxed">
            Camera not working? <strong>Upload</strong> a screenshot of someone's FinVault QR — or paste the 10-digit number directly on the Transfer page.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
